import { useEffect, useMemo, useState, type MutableRefObject, type ReactNode } from 'react';
import { scrollToSection } from './QuoteSection';
import { tabIdOf, toTabs } from './QuoteTabs';
import { TabsLayout } from './TabsLayout';
import type { QuoteLayoutSection } from '../types';

/**
 * The quote's sections, behind a tab strip.
 *
 * The sections are declared ONCE by the caller, as a list carrying its own
 * content, and this arranges them. A page branching on the arrangement around
 * its own JSX would have meant two copies of nine sections drifting apart, which
 * is the thing this package exists to prevent.
 *
 * There used to be a second arrangement — one long column with an index rail
 * down the left — and a toggle between them. It went because a page with two
 * shapes is two pages to keep right, and every question about this one ("why is
 * the sticky rail here", "why does the table scroll") had to be answered twice.
 *
 * ── What lives here and what doesn't ──
 *
 * This owns the state the layout needs and the caller shouldn't: which tab is
 * open, and the reveal handle. `TabsLayout` is then purely arrangement — it
 * takes an answer and draws it.
 *
 * It covers the section area and nothing else. A page's header, its stat strip
 * and its modals sit outside, because wrapping something that never varies is
 * indirection for its own sake.
 */
export function QuoteSections({
	sections,
	lead,
	revealRef,
	className,
}: {
	sections: QuoteLayoutSection[];
	/** Page-level content above the sections — an alert that has to be seen
	 * whichever part of the quote is open. */
	lead?: ReactNode;
	/**
	 * Filled with "take me to this section", for anything outside the sections
	 * that points at one — a banner whose fix is three sections down.
	 *
	 * Scrolling to the anchor alone does nothing: the target is very likely
	 * inside a hidden panel, so its tab has to open first. That knowledge lives
	 * here rather than at the call site.
	 *
	 * It's a ref rather than context because the things that need it — `lead`,
	 * a header action — are props evaluated in the caller, outside any provider
	 * this could put around its children.
	 */
	revealRef?: MutableRefObject<((sectionId: string) => void) | null>;
	className?: string;
}) {
	const tabs = useMemo(() => toTabs(sections), [sections]);
	const [openTab, setOpenTab] = useState(() => tabs[0]?.id ?? '');

	/* The list moves with the data — a quote that loses its last vetter loses the
	   section. If the open tab goes with it, fall to the first rather than
	   rendering an empty page. */
	const tabKey = tabs.map((tab) => tab.id).join('|');
	useEffect(() => {
		const ids = tabKey ? tabKey.split('|') : [];
		if (ids.length > 0 && !ids.includes(openTab)) setOpenTab(ids[0]);
	}, [tabKey, openTab]);

	/* Assigned every render: it closes over `sections`, which changes freely. */
	useEffect(() => {
		if (!revealRef) return;
		revealRef.current = (sectionId: string) => {
			const section = sections.find((candidate) => candidate.id === sectionId);
			if (!section) return;
			setOpenTab(tabIdOf(section));
			/* A panel that just went from hidden to shown has no box until React
			   commits, and scrolling to a `display: none` element does nothing. */
			requestAnimationFrame(() => scrollToSection(sectionId));
		};
		return () => {
			revealRef.current = null;
		};
	});

	return (
		<TabsLayout
			sections={sections}
			tabs={tabs}
			openTab={openTab}
			onOpenTab={setOpenTab}
			lead={lead}
			className={className}
		/>
	);
}
